
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SolutionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: number;
}

const SolutionCard: React.FC<SolutionCardProps> = ({ title, description, icon, delay }) => {
  return (
    <div
      className="p-6 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in backdrop-blur-sm bg-white/70"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="bg-gradient-attaboy rounded-lg p-3 inline-flex text-white mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-3 font-poppins">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const SolutionsSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-36 relative overflow-hidden" id="solutions">
      {/* Background with soft gradient instead of diagonal lines */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23EF3E2C\' fill-opacity=\'0.4\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'3\'/%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E")',
        }}></div>
      </div>

      {/* Decorative Circles */}
      <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-attaboy/5 rounded-full filter blur-3xl"></div>
      <div className="absolute -left-20 top-20 w-64 h-64 bg-attaboy-light/10 rounded-full filter blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">{t('solutions.title')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('solutions.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Marketing Solution */}
          <SolutionCard
            title={t('solutions.marketing.title')}
            description={t('solutions.marketing.desc')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            }
            delay={100}
          />

          {/* Finance Solution */}
          <SolutionCard
            title={t('solutions.finance.title')}
            description={t('solutions.finance.desc')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            delay={200}
          />

          {/* Legal Solution */}
          <SolutionCard
            title={t('solutions.legal.title')}
            description={t('solutions.legal.desc')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            }
            delay={300}
          />

          {/* SMB Solution */}
          <SolutionCard
            title={t('solutions.smb.title')}
            description={t('solutions.smb.desc')}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
            delay={400}
          />
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;
